import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { Box, Typography } from '@mui/material';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';

import {
  OnboardingModalActions,
  useOnboardingModalActionsCompact,
} from '@/components/MainModal/OnboardingModalActions';
import { OnboardingSSOFlowModel } from '@/features/onboarding-sso/models/onboarding-sso-flow.model';
import {
  selectCompanyId,
  selectCompanyName,
  selectHubspotContactId,
  selectUserEmail,
  setHubspotContactId,
} from '@/redux/features/authSlice';
import { setCompanyName, setCompanySize } from '@/redux/features/onboardingSlice';
import CompanyRequests from '@/requests/company.requests';
import HubspotRequests from '@/requests/hubspot.requests';
import { BasicCard } from '@/shared/BasicCard';
import { LoadingButton } from '@/shared/Button';
import { TextField } from '@/shared/TextField';

import { useAppDispatch, useAppSelector, useOnboardingTask, useTracking, useTraits } from '../../../hooks';
import { CompanySizeStep, StepCommonProps, StepInfo } from '../../../types';
import SentryService from '../../../utils/sentry-service.util';

import styles from './CompanySizeStep.module.scss';

export const newCompanySizes = [
  { key: '1-10', label: '1-10' },
  { key: 'n11_50', label: '11-50' },
  { key: 'n51_200', label: '51-200' },
  { key: 'n201_500', label: '201-500' },
  { key: '501-1000', label: '501-1000' },
  { key: '1001+', label: '1001' },
];

const CompanySizeStepComponent: FC<StepCommonProps> = ({ flow }) => {
  const { goToNextStep, getNextStep } = useOnboardingTask(flow);
  const { trackOnboardingStep } = useTracking(flow);
  const { updateTraits } = useTraits();
  const growthBook = useGrowthBook();
  const dispatch = useAppDispatch();
  const { company } = useAppSelector(state => state.auth);
  const companyId = useSelector(selectCompanyId);
  const hubspotContactId = useSelector(selectHubspotContactId);
  const userEmail = useSelector(selectUserEmail);
  const existingCompanyName = useSelector(selectCompanyName);
  const { t } = useTranslation('onboarding');
  const compactActions = useOnboardingModalActionsCompact();
  const storeCompanySize = useAppSelector(state => state.onboarding.companySize);
  const isGoogleAuthUser = company?.splitTest?.some(st => st.name === 'google-auth' && st.value);
  const showCompanyNameField = !!isGoogleAuthUser;


  const backendCompanySize = company?.companySettings?.custom?.companySize ?? storeCompanySize;
  const initialSelectedCard = newCompanySizes.find(size => size.key === backendCompanySize)?.key ?? null;

  const [selectedCardKey, setSelectedCardKey] = useState<string | null>(initialSelectedCard);
  const [isCardClicked, setIsCardClicked] = useState<boolean>(!!initialSelectedCard);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyNameInput, setCompanyNameInput] = useState(showCompanyNameField ? (existingCompanyName || '') : '');

  const hasTrackedCompanySize = useRef(false);
  const lastSyncedHubspotSize = useRef<string | null>(null);

  const syncHubspotCompanySize = useCallback(async (sizeKey: string) => {
    if (!isGoogleAuthUser || !sizeKey) return;

    if (lastSyncedHubspotSize.current === sizeKey) return;

    const contactId = hubspotContactId
      || (userEmail ? await HubspotRequests.getContactIdByEmail(userEmail) : undefined);

    if (!contactId) return;

    try {
      lastSyncedHubspotSize.current = sizeKey;
      await HubspotRequests.updateContactProperties(contactId, { tdpeopleinthecompany: sizeKey });

      if (!hubspotContactId) {
        dispatch(setHubspotContactId(contactId));
      }
    } catch (error) {
      lastSyncedHubspotSize.current = null;
      await SentryService.captureExceptionWithRetryAfter(error, {
        errorContext: 'Failed to sync HubSpot company size',
        contactId,
        sizeKey,
      });
    }
  }, [dispatch, hubspotContactId, isGoogleAuthUser, userEmail]);

  const syncHubspotCompanyName = useCallback(async (name: string) => {
    if (!isGoogleAuthUser || !name) return;

    const contactId = hubspotContactId
      || (userEmail ? await HubspotRequests.getContactIdByEmail(userEmail) : undefined);

    if (!contactId) return;

    try {
      await HubspotRequests.updateContactProperties(contactId, { company: name });

      if (!hubspotContactId) {
        dispatch(setHubspotContactId(contactId));
      }
    } catch (error) {
      await SentryService.captureExceptionWithRetryAfter(error, {
        errorContext: 'Failed to sync HubSpot company name',
        contactId,
        name,
      });
    }
  }, [dispatch, hubspotContactId, isGoogleAuthUser, userEmail]);

  // Sync company size if available from store/backend
  useEffect(() => {
    const latestSize = storeCompanySize ?? company?.companySettings?.custom?.companySize;
    if (latestSize && latestSize !== selectedCardKey) {
      setSelectedCardKey(latestSize);
      setIsCardClicked(true);
    }
  }, []);


  async function onCardClicked(cardKey: string) {
    setSelectedCardKey(cardKey);
    setIsCardClicked(true);
    dispatch(setCompanySize(cardKey));
    (flow as OnboardingSSOFlowModel).setCompanySize(cardKey);

    growthBook?.setAttributes({
      ...growthBook?.getAttributes(),
      estimated_company_size: cardKey,
    });

    CompanyRequests.putCompanySettings(companyId, {
      custom: { companySize: cardKey },
    });

    updateTraits({
      companyTraits: {
        tdpeopleinthecompany: cardKey,
        onboarding_step: getNextStep()?.stepName,
      },
    });

    await syncHubspotCompanySize(cardKey);
  }

  const trackSelectedCompanySize = () => {
    if (hasTrackedCompanySize.current || !selectedCardKey) return;

    trackOnboardingStep('Company Name and Size', {
      companysize: selectedCardKey,
    });

    hasTrackedCompanySize.current = true;
  };

  const handleNextClick = async () => {
    if (isSubmitting || !isCardClicked) return;
    if (showCompanyNameField && !companyNameInput.trim()) return;
    setIsSubmitting(true);

    if (showCompanyNameField && companyNameInput.trim()) {
      const trimmedName = companyNameInput.trim();
      const nameChanged = trimmedName !== existingCompanyName;

      if (nameChanged) {
        dispatch(setCompanyName(trimmedName));
        await Promise.all([
          CompanyRequests.putCompanySettings(companyId, {
            name: trimmedName,
            custom: company?.companySettings?.custom || {},
          }),
          syncHubspotCompanyName(trimmedName),
          updateTraits({
            companyTraits: { name: trimmedName },
            userTraits: { workspaceName: trimmedName },
          }),
        ]);
      }
    }

    trackSelectedCompanySize();

    goToNextStep();
    setIsSubmitting(false);
  };

  const companySizesToShow = newCompanySizes;

  return (
    <div className={ styles.CompanySizeStep }>
      { showCompanyNameField && (
        <Box width="80%" mb={ 1 }>
          <TextField
            label={ t('company_size_step.company_name_label') }
            variant="filled"
            fullWidth
            value={ companyNameInput }
            onChange={ (e) => setCompanyNameInput(e.target.value) }
            inputProps={{ 'data-testid': 'company-name-input' }}
          />
        </Box>
      ) }

      <Box width="80%" mb={ 1 }>
        <Typography variant="body1">
          { t('company_size_step.company_size_label') }
        </Typography>
      </Box>

      { companySizesToShow.map((size, index) => {
        const isSelected = selectedCardKey === size.key;

        return (
          <BasicCard
            key={ size.key }
            cardKey={ size.key }
            onCardClicked={ () => onCardClicked(size.key) }
            className={ `BasicCard ${ isSelected ? styles.Selected : '' }` }
            css={ css`
              width: 126px;
              cursor: pointer;
              box-sizing: border-box;
              border: 2px solid ${ isSelected ? '#0066FF' : 'transparent' };
              border-radius: 4px;

              ${ isSelected &&
            `
                box-shadow: none;

                &:hover {
                  background-color: #FFF !important;
                }
              ` }

              ${ index === companySizesToShow.length - 1 &&
            `
                .content::after {
                  content: " or more";

                  @media (max-width: 600px) {
                    content: "+";
                  }
                }
              ` }
            ` }
          >
            <span className="content | d-block txt-center color-pure-black">{ size.label }</span>
          </BasicCard>
        );
      }) }

      <OnboardingModalActions>
        <Box
          sx={ compactActions ? { pt: 0 } : { pt: 3 } }
          display="flex"
          alignItems="flex-end"
          justifyContent="flex-end"
          width="100%"
        >
          <LoadingButton
            variant="contained"
            type="button"
            disableElevation={ false }
            disabled={ !isCardClicked || isSubmitting || (showCompanyNameField && !companyNameInput.trim()) }
            loading={ isSubmitting }
            onClick={ handleNextClick }
            css={ css`margin-bottom: 4px` }
          >
            { t('common:next') }
          </LoadingButton>
        </Box>
      </OnboardingModalActions>
    </div>
  );
};

export const CompanySizeStepInfo: StepInfo<CompanySizeStep> = {
  Component: CompanySizeStepComponent,
  id: 'companyInfoStep',
  stepName: 'About You Screen',
  title: 'onboarding:company_size_step.title',
  Info: <Trans i18nKey="onboarding:company_size_step.info" />,
};
